/**
 * Tenant-administration capability booleans for the caller, derived from
 * the permission slugs `/api/me` serializes on `membership.permissions`.
 *
 * These drive which Settings tabs render at all — plus, since project
 * creation became a gated verb, the create-project affordances outside
 * Settings. A tab (or a tile) whose every action 403s is worse than none
 * at all: it advertises a capability the user doesn't have and costs
 * them a click to find that out. So the management tabs
 * (Users / Roles / Clients / Teams / My Organization) are gated on the
 * same slug the BE checks, and the profile-scoped tabs (My Profile,
 * Notifications) stay open to everyone so no role lands on an empty page.
 *
 * Gate on SLUGS, not on `membership.role` — same reasoning as
 * `use-card-permissions.ts`: an owner can edit any system role's
 * permission set in Settings → Roles, so a role-derived switch goes
 * silently wrong the moment a tenant customizes one. Note the seeded
 * grants are NOT the tidy ladder the role names suggest: `admin` holds
 * every slug EXCEPT `manage-org` (migrations `0015` + `0082`), so My
 * Organization is owner-only by default while Roles reaches both.
 *
 * Fall-open contract, identical to `cardPermissionsFromMe`: an ABSENT
 * `permissions` array means an older BE that doesn't serialize the field
 * (or `useMe` still in flight), so every capability reads `true` and the
 * BE's 403 remains the backstop — a FE-first deploy is a no-op rather
 * than a lockout. A PRESENT but empty array is a real answer and falls
 * closed.
 */

import { useMemo } from 'react'

import type { Me } from '@/api/types-bridge'
import { useMe } from './use-me'

export interface TenantPermissions {
  /** Invite, edit, deactivate users. Seeded to admin + owner. */
  canManageUsers: boolean
  /** Create/edit/delete roles and their permission sets. Seeded to admin + owner. */
  canManageRoles: boolean
  /** Full client CRUD. Seeded to admin + owner. */
  canManageClients: boolean
  /** Full team + membership CRUD. Seeded to admin + owner. */
  canManageTeams: boolean
  /** Edit org name, logo, and org-wide settings. Seeded to owner only. */
  canManageOrg: boolean
  /**
   * Connect, check and disconnect the code hosts (GitHub, AWS CodeCommit)
   * and read their repository list. Seeded to owner + admin; gates the
   * Settings → Integrations tab.
   */
  canManageIntegrations: boolean
  /**
   * Create a project. Seeded to owner + admin ONLY (migration `0015`),
   * so this is the one capability here that is narrower than "the
   * management tabs" and it gates a control outside Settings entirely.
   *
   * The slug had been seeded since `0015` with nothing ever checking it,
   * so every rung could create a project until the BE started enforcing
   * it. Withholding the affordance from `manager` / `member` /
   * `commenter` / `reader` is therefore a deliberate behaviour change,
   * not a bug fix — decided rather than inherited.
   */
  canCreateProjects: boolean
}

const ALL_ALLOWED: TenantPermissions = {
  canManageUsers: true,
  canManageRoles: true,
  canManageClients: true,
  canManageTeams: true,
  canManageOrg: true,
  canManageIntegrations: true,
  canCreateProjects: true,
}

export function tenantPermissionsFromMe(me: Me | undefined): TenantPermissions {
  const slugs = me?.membership?.permissions
  if (slugs == null) return ALL_ALLOWED

  return {
    canManageUsers: slugs.includes('manage-users'),
    canManageRoles: slugs.includes('manage-roles'),
    canManageClients: slugs.includes('manage-clients'),
    canManageTeams: slugs.includes('manage-teams'),
    canManageOrg: slugs.includes('manage-org'),
    canManageIntegrations: slugs.includes('manage-integrations'),
    canCreateProjects: slugs.includes('create-projects'),
  }
}

export function useTenantPermissions(): TenantPermissions {
  const me = useMe()
  return useMemo(() => tenantPermissionsFromMe(me.data), [me.data])
}

/**
 * The caller's raw permission slug set, or `null` when `/api/me` hasn't
 * answered (or serves an older shape without the field).
 *
 * The booleans above answer "may I open this tab"; this answers "which
 * individual slugs may I hand out", which the role editor needs because
 * the BE refuses any role write whose permission diff touches a slug the
 * caller doesn't hold. `null` is the same fall-open contract: unknown
 * means don't lock anything, and the BE's 403 stays the backstop.
 */
export function useHeldPermissionSlugs(): ReadonlySet<string> | null {
  const me = useMe()
  return useMemo(() => {
    const slugs = me.data?.membership?.permissions
    return slugs == null ? null : new Set(slugs)
  }, [me.data])
}
